import { isRemix } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import type { SelfCheckoutStepKey } from "./selfCheckoutFlow";

/**
 * Path patterns identifying the direct-lab (labs_self_checkout) flow.
 *
 * Single source of truth for the labs path shape — `@checkout/patient`'s
 * `checkoutRoutes` re-uses these patterns, and `useSelfCheckoutFlow` derives
 * the active flow from them. Lives in `@shared` (the only package both the
 * unauthenticated and checkout bundles may import per the cross-reference
 * rule) so flow resolution never couples the bundles.
 *
 * Matches both surfaces:
 * - unauthenticated Rails entry: /checkout/:store_slug/labs/test|panel/:id
 * - authenticated Remix checkout: /r/checkout/:store_slug/labs/test|panel/:id
 */
const DIRECT_LABS_SELF_CHECKOUT_PATH_PATTERN =
  /\/labs\/(test|panel)\/[^/]+(?:\/(?:payment|sample-collection))?\/?$/;

const CHECKOUT_BASE_PATTERN = /^\/(?:r\/)?checkout\//;

const isDirectLabsSelfCheckoutPathname = (pathname: string): boolean => {
  return (
    CHECKOUT_BASE_PATTERN.test(pathname) && DIRECT_LABS_SELF_CHECKOUT_PATH_PATTERN.test(pathname)
  );
};

/** In Remix, checkout routes are served under /r/checkout instead of /checkout. */
const getCheckoutBasePath = (): string => (isRemix() ? "/r/checkout" : "/checkout");

/**
 * URL segment each step owns, `null` for the steps that have none.
 *
 * `patientInfo` and `address` both resolve to the step-less index route, which
 * renders the combined patient-info + address form — the display list has two
 * steps there, the router has one URL.
 */
const SELF_CHECKOUT_STEP_SEGMENTS: Record<SelfCheckoutStepKey, string | null> = {
  patientInfo: null,
  address: null,
  sampleCollection: "sample-collection",
  payment: "payment",
};

/**
 * Identifies a self-checkout surface. Discriminated so direct-lab cannot be
 * built without its `item_type`, which would yield a route that matches nothing.
 */
type SelfCheckoutRoute =
  | { flow: "journey"; storeSlug: string; id: string }
  | { flow: "directLab"; storeSlug: string; id: string; itemType: string };

/**
 * URL for `step` on the given surface. Steps without a segment of their own
 * yield the index route, so callers never have to special-case them.
 */
const buildSelfCheckoutStepPath = (route: SelfCheckoutRoute, step: SelfCheckoutStepKey): string => {
  const base =
    route.flow === "journey"
      ? `${getCheckoutBasePath()}/${route.storeSlug}/journey/${route.id}`
      : `${getCheckoutBasePath()}/${route.storeSlug}/labs/${route.itemType}/${route.id}`;
  const segment = SELF_CHECKOUT_STEP_SEGMENTS[step];

  return segment ? `${base}/${segment}` : base;
};

export type { SelfCheckoutRoute };
export {
  buildSelfCheckoutStepPath,
  DIRECT_LABS_SELF_CHECKOUT_PATH_PATTERN,
  getCheckoutBasePath,
  isDirectLabsSelfCheckoutPathname,
  SELF_CHECKOUT_STEP_SEGMENTS,
};
